import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Plus, Trash2, Clock, Layers, FileText, ChevronDown, ChevronUp, AlertTriangle, Check, ChevronsUpDown, Tag } from 'lucide-react';
import { orderBlocksChronologically } from '../lib/timeBlocks';

const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES_5 = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

function hhmmToMinutes(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function calcBlockHours(start, end, breakMins = 0) {
  if (!start || !end) return 0;
  let startMin = hhmmToMinutes(start);
  let endMin = hhmmToMinutes(end);
  if (endMin <= startMin) endMin += 24 * 60;
  return Math.max(0, (endMin - startMin - breakMins) / 60);
}

/** Detect which block indices are involved in at least one overlap. */
function detectOverlaps(blocks) {
  if (!blocks || blocks.length < 2) return { overlapping: new Set(), messages: [] };
  const ordered = orderBlocksChronologically(blocks);

  const overlapping = new Set();
  const messages = [];
  for (let a = 0; a < ordered.length; a++) {
    for (let b = a + 1; b < ordered.length; b++) {
      if (ordered[b]._offset_start < ordered[a]._offset_end) {
        const origA = ordered[a]._orig_idx;
        const origB = ordered[b]._orig_idx;
        overlapping.add(origA);
        overlapping.add(origB);
        const mins = Math.min(ordered[a]._offset_end, ordered[b]._offset_end) - ordered[b]._offset_start;
        messages.push(
          `Block ${origA + 1} (${blocks[origA].start_time}\u2013${blocks[origA].end_time}) overlaps with ` +
          `Block ${origB + 1} (${blocks[origB].start_time}\u2013${blocks[origB].end_time}) by ${mins} min`
        );
      }
    }
  }
  return { overlapping, messages };
}

function TimeSelect({ value, onChange, testId }) {
  const hh = value?.split(':')[0] || '07';
  const mm = value?.split(':')[1] || '00';
  return (
    <div className="flex gap-1 items-center">
      <Select value={hh} onValueChange={(h) => onChange(`${h}:${mm}`)}>
        <SelectTrigger className="w-16 h-8 text-xs font-mono" data-testid={testId ? `${testId}-hh` : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{HOURS_24.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
      </Select>
      <span className="text-sm font-bold">:</span>
      <Select value={mm} onValueChange={(m) => onChange(`${hh}:${m}`)}>
        <SelectTrigger className="w-16 h-8 text-xs font-mono" data-testid={testId ? `${testId}-mm` : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{MINUTES_5.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/** MYOB Job searchable combobox */
function MyobJobPicker({ value, myobJobs, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = myobJobs.find(j => j.uid === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-7 text-xs justify-between w-full font-normal"
          data-testid={testId}
        >
          <span className="truncate">
            {selected ? `${selected.number} – ${selected.name}` : 'Select MYOB job...'}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search jobs..." className="text-xs" />
          <CommandList>
            <CommandEmpty className="text-xs p-2 text-center text-muted-foreground">No jobs found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__clear__"
                onSelect={() => { onChange(''); setOpen(false); }}
                className="text-xs text-muted-foreground"
              >
                <span>Use default</span>
                {!value && <Check className="ml-auto h-3 w-3" />}
              </CommandItem>
              {myobJobs.map(j => (
                <CommandItem
                  key={j.uid}
                  value={`${j.number} ${j.name}`}
                  onSelect={() => { onChange(j.uid); setOpen(false); }}
                  className="text-xs"
                >
                  <span className="font-mono mr-1">{j.number}</span> – {j.name}
                  {value === j.uid && <Check className="ml-auto h-3 w-3" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function TimeBlockEditor({
  blocks = [],
  onBlocksChange,
  readOnly = false,
  compact = false,
  trackingCategories = [],
  connectedPlatform = null,
  trackingDefaults = {},
  timesheetCategoryId = null,
  myobJobs = [],
  connectionId = null,
}) {
  const [expandedBlock, setExpandedBlock] = useState(null);

  const { overlapping, messages: overlapMessages } = useMemo(() => detectOverlaps(blocks), [blocks]);

  // Determine which categories to show for Xero
  const xeroCategoriesToShow = useMemo(() => {
    if (connectedPlatform !== 'xero' || !trackingCategories.length) return [];
    if (timesheetCategoryId) {
      // Show only the timesheet category
      const cat = trackingCategories.find(c => c.id === timesheetCategoryId);
      return cat ? [cat] : [];
    }
    // Legacy: show first 2
    return trackingCategories.slice(0, 2);
  }, [connectedPlatform, trackingCategories, timesheetCategoryId]);

  const updateBlock = (index, field, value) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], [field]: value };
    if (['start_time', 'end_time', 'break_minutes'].includes(field)) {
      updated[index].hours = parseFloat(
        calcBlockHours(updated[index].start_time, updated[index].end_time, updated[index].break_minutes || 0).toFixed(2)
      );
    }
    onBlocksChange(updated);
  };

  const addBlock = () => {
    const lastBlock = blocks[blocks.length - 1];
    const newStart = lastBlock?.end_time || '07:00';
    const newEnd = '17:00';
    const newBlock = {
      block_id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      job_id: null,
      job_number: null,
      customer_name: '',
      start_time: newStart,
      end_time: newEnd,
      break_minutes: 0,
      hours: calcBlockHours(newStart, newEnd, 0),
      is_unallocated: true,
      tracking: {},
      notes: '',
    };
    onBlocksChange([...blocks, newBlock]);
  };

  const removeBlock = (index) => {
    onBlocksChange(blocks.filter((_, i) => i !== index));
  };

  const totalHours = blocks.reduce((sum, b) => sum + (b.hours || 0), 0);

  const hasTrackingPickers = connectedPlatform === 'xero' ? xeroCategoriesToShow.length > 0 : connectedPlatform === 'myob';

  return (
    <div className="space-y-2" data-testid="time-block-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#ffd108]" />
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Time Blocks
          </Label>
          <Badge variant="outline" className="text-xs font-mono">
            {blocks.length} block{blocks.length !== 1 ? 's' : ''} &bull; {totalHours.toFixed(2)}h
          </Badge>
        </div>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={addBlock}
            data-testid="add-time-block-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Block
          </Button>
        )}
      </div>

      {/* Overlap warnings */}
      {overlapMessages.length > 0 && (
        <div className="rounded-md border border-red-500/40 bg-red-500/5 px-3 py-2 space-y-1" data-testid="block-overlap-warning">
          <div className="flex items-center gap-1.5 text-red-500 text-xs font-semibold">
            <AlertTriangle className="h-3.5 w-3.5" />
            Overlapping Blocks Detected
          </div>
          {overlapMessages.map((msg, i) => (
            <p key={i} className="text-[11px] text-red-400/80 pl-5">{msg}</p>
          ))}
        </div>
      )}

      {/* Block Cards */}
      <div className="space-y-2">
        {blocks.map((block, idx) => {
          const blockTrackingLabel = _getBlockTrackingLabel(block, connectedPlatform, trackingCategories, myobJobs, timesheetCategoryId);

          return (
          <div
            key={block.block_id || idx}
            className={`rounded-lg border p-3 transition-all ${
              overlapping.has(idx)
                ? 'border-red-500/60 bg-red-500/5 ring-1 ring-red-500/20'
                : block.is_unallocated
                  ? 'border-dashed border-amber-500/50 bg-amber-500/5'
                  : 'border-border bg-card'
            }`}
            data-testid={`time-block-${idx}`}
          >
            {/* Block header row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {block.is_unallocated ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] shrink-0">
                    Unallocated
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    {block.job_number ? `#${block.job_number}` : 'Job'}
                  </Badge>
                )}
                {overlapping.has(idx) && (
                  <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />
                )}
                {block.customer_name && (
                  <span className="text-xs text-muted-foreground truncate">{block.customer_name}</span>
                )}
                {/* Show tracking chip on header when collapsed */}
                {blockTrackingLabel && expandedBlock !== idx && (
                  <Badge variant="outline" className="text-[10px] text-indigo-500 border-indigo-400/30 shrink-0">
                    <Tag className="h-2.5 w-2.5 mr-0.5" />{blockTrackingLabel}
                  </Badge>
                )}
                <div className="flex items-center gap-1 ml-auto text-xs font-mono">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span>{block.start_time || '--:--'}</span>
                  <span className="text-muted-foreground">-</span>
                  <span>{block.end_time || '--:--'}</span>
                  <span className="text-muted-foreground ml-1">({(block.hours || 0).toFixed(2)}h)</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {!readOnly && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setExpandedBlock(expandedBlock === idx ? null : idx)}
                    >
                      {expandedBlock === idx ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-500 hover:text-red-700"
                      onClick={() => removeBlock(idx)}
                      data-testid={`remove-block-${idx}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Expanded edit area */}
            {(expandedBlock === idx || (readOnly && block.notes)) && (
              <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
                {!readOnly && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Start</Label>
                        <TimeSelect
                          value={block.start_time}
                          onChange={(v) => updateBlock(idx, 'start_time', v)}
                          testId={`block-${idx}-start`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">End</Label>
                        <TimeSelect
                          value={block.end_time}
                          onChange={(v) => updateBlock(idx, 'end_time', v)}
                          testId={`block-${idx}-end`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-muted-foreground">Break (mins)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={block.break_minutes || 0}
                        onChange={(e) => updateBlock(idx, 'break_minutes', parseInt(e.target.value) || 0)}
                        className="h-8 text-xs w-24"
                        data-testid={`block-${idx}-break`}
                      />
                    </div>
                  </>
                )}
                {/* Xero tracking selectors */}
                {connectedPlatform === 'xero' && xeroCategoriesToShow.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[10px] text-muted-foreground font-semibold flex items-center gap-1">
                      <Tag className="h-3 w-3" /> Xero Tracking
                    </Label>
                    {xeroCategoriesToShow.map((cat) => (
                      <div key={cat.id} className="space-y-0.5">
                        <Label className="text-[10px] text-muted-foreground">{cat.name}</Label>
                        <Select
                          value={
                            (block.tracking?.xero || []).find(t => t.category_id === cat.id)?.option_id || '__default__'
                          }
                          onValueChange={(optId) => {
                            if (optId === '__default__') {
                              // Clear selection for this category
                              const existing = (block.tracking?.xero || []).filter(t => t.category_id !== cat.id);
                              updateBlock(idx, 'tracking', {
                                ...block.tracking,
                                xero: existing.length > 0 ? existing : undefined,
                                connection_id: connectionId,
                              });
                              return;
                            }
                            const opt = cat.options.find(o => o.id === optId);
                            const existing = (block.tracking?.xero || []).filter(t => t.category_id !== cat.id);
                            const newTracking = {
                              ...block.tracking,
                              xero: [...existing, {
                                category_id: cat.id,
                                category_name: cat.name,
                                option_id: optId,
                                option_name: opt?.name || '',
                              }],
                              connection_id: connectionId,
                            };
                            updateBlock(idx, 'tracking', newTracking);
                          }}
                          disabled={readOnly}
                        >
                          <SelectTrigger className="h-7 text-xs" data-testid={`block-${idx}-xero-tracking-${cat.id}`}>
                            <SelectValue placeholder={`Select ${cat.name}...`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__default__">Use default</SelectItem>
                            {cat.options.filter(o => o.status === 'ACTIVE').map(opt => (
                              <SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                    {!blockTrackingLabel && (
                      <p className="text-[10px] text-muted-foreground/60 italic">Uses rule or default at export</p>
                    )}
                  </div>
                )}
                {/* MYOB job picker (searchable combobox) */}
                {connectedPlatform === 'myob' && myobJobs.length > 0 && (
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground font-semibold flex items-center gap-1">
                      <Tag className="h-3 w-3" /> MYOB Job
                    </Label>
                    {readOnly ? (
                      <p className="text-xs text-muted-foreground">
                        {(() => {
                          const j = myobJobs.find(j => j.uid === block.tracking?.myob_job_uid);
                          return j ? `${j.number} – ${j.name}` : block.tracking?.myob_job_uid ? 'Unknown job' : '—';
                        })()}
                      </p>
                    ) : (
                      <MyobJobPicker
                        value={block.tracking?.myob_job_uid || ''}
                        myobJobs={myobJobs}
                        onChange={(uid) => updateBlock(idx, 'tracking', {
                          ...block.tracking,
                          myob_job_uid: uid || undefined,
                          connection_id: connectionId,
                        })}
                        testId={`block-${idx}-myob-job`}
                      />
                    )}
                    {!block.tracking?.myob_job_uid && (
                      <p className="text-[10px] text-muted-foreground/60 italic">Uses rule or default at export</p>
                    )}
                  </div>
                )}
                {/* Hint when no tracking pickers visible but tracking is possible */}
                {!hasTrackingPickers && connectedPlatform && (
                  <p className="text-[10px] text-muted-foreground/60 italic">Uses rule or default at export</p>
                )}
                <div className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">Notes</Label>
                  {readOnly ? (
                    <p className="text-xs text-muted-foreground">{block.notes || '-'}</p>
                  ) : (
                    <Textarea
                      value={block.notes || ''}
                      onChange={(e) => updateBlock(idx, 'notes', e.target.value)}
                      rows={1}
                      className="text-xs min-h-[32px]"
                      placeholder="Block notes..."
                      data-testid={`block-${idx}-notes`}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          );
        })}

        {blocks.length === 0 && (
          <div className="text-center py-4 text-xs text-muted-foreground border border-dashed rounded-lg">
            <FileText className="h-5 w-5 mx-auto mb-1 opacity-50" />
            No time blocks. Use &quot;Auto-fill from Dockets&quot; or add blocks manually.
          </div>
        )}
      </div>
    </div>
  );
}

/** Get a human-readable tracking label for a block. */
function _getBlockTrackingLabel(block, platform, trackingCategories, myobJobs, timesheetCategoryId) {
  if (!platform) return null;
  const t = block.tracking || {};
  if (platform === 'xero' && t.xero?.length > 0) {
    // If timesheetCategoryId, show only matching
    const entry = timesheetCategoryId
      ? t.xero.find(e => e.category_id === timesheetCategoryId)
      : t.xero[0];
    return entry?.option_name || null;
  }
  if (platform === 'myob' && t.myob_job_uid) {
    const job = myobJobs.find(j => j.uid === t.myob_job_uid);
    return job ? `${job.number} – ${job.name}` : 'Unknown job';
  }
  return null;
}

export function TimeBlockReadOnly({ blocks = [], trackingCategories = [], connectedPlatform = null, myobJobs = [], timesheetCategoryId = null }) {
  if (!blocks || blocks.length === 0) return null;
  const totalHours = blocks.reduce((sum, b) => sum + (b.hours || 0), 0);

  return (
    <div className="space-y-2" data-testid="time-blocks-readonly">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-[#ffd108]" />
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Time Blocks
        </Label>
        <Badge variant="outline" className="text-xs font-mono">
          {blocks.length} block{blocks.length !== 1 ? 's' : ''} &bull; {totalHours.toFixed(2)}h
        </Badge>
      </div>
      <div className="space-y-1.5">
        {blocks.map((block, idx) => {
          const label = _getBlockTrackingLabel(block, connectedPlatform, trackingCategories, myobJobs, timesheetCategoryId);
          return (
            <div
              key={block.block_id || idx}
              className={`rounded-md border p-2 text-xs ${
                block.is_unallocated
                  ? 'border-dashed border-amber-500/50 bg-amber-500/5'
                  : 'border-border bg-muted/20'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  {block.is_unallocated ? (
                    <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px]">Unalloc</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      {block.job_number ? `#${block.job_number}` : 'Job'}
                    </Badge>
                  )}
                  {block.customer_name && (
                    <span className="text-muted-foreground truncate max-w-[120px]">{block.customer_name}</span>
                  )}
                  {label && (
                    <Badge variant="outline" className="text-[10px] text-indigo-500 border-indigo-400/30">
                      <Tag className="h-2.5 w-2.5 mr-0.5" />{label}
                    </Badge>
                  )}
                  {block.tracking_rule_name && (
                    <Badge variant="outline" className="text-[10px] text-green-500 border-green-400/30">
                      {block.tracking_rule_name}
                    </Badge>
                  )}
                </div>
                <span className="font-mono text-muted-foreground">
                  {block.start_time}-{block.end_time} ({(block.hours || 0).toFixed(2)}h)
                </span>
              </div>
              {block.notes && (
                <p className="text-[10px] text-muted-foreground mt-1 pl-1 border-l-2 border-muted">{block.notes}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
